import { useMemo, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Droplets, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { useDiary } from "../../lib/diary";
import { toInput } from "../../lib/irrigation";

const WD = ["L", "M", "X", "J", "V", "S", "D"];
const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

// Month grid: Monday-first, with leading blanks.
const monthDays = (m) => {
  const first = monthStart(m);
  const lead = (first.getDay() + 6) % 7;
  const n = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
  return [...Array(lead).fill(null), ...Array.from({ length: n }, (_, i) => new Date(m.getFullYear(), m.getMonth(), i + 1))];
};

const tone = { done: "bg-[#8CD05A] text-[#0E2A1C] border-[#8CD05A]", extra: "bg-[#8CD05A]/50 text-[#0E2A1C] border-[#8CD05A]",
  missed: "bg-[#C8452E]/25 border-[#E0624B] text-[#F6EEDC]", planned: "bg-[#1B3A8C]/60 border-[#5B7FE0]/60 text-[#F6EEDC]",
  rest: "border-[#F6EEDC]/10 text-[#C9D6C2]", out: "border-transparent text-[#C9D6C2]/75" };

const Legend = () => (
  <div className="flex flex-wrap gap-3 text-[11px] text-[#C9D6C2]">
    {[["planned", "Previsto"], ["done", "Hecho"], ["missed", "Olvidado"], ["extra", "Riego extra"]].map(([k, l]) => (
      <span key={k} className="inline-flex items-center gap-1.5"><span className={`w-3 h-3 rounded-[3px] border ${tone[k]}`} />{l}</span>
    ))}
  </div>
);

export const JWaterCalendar = ({ weeks }) => {
  const [rows, setRows] = useDiary();
  const todayStr = toInput(new Date());
  const plan = useMemo(() => {
    const map = {};
    weeks.forEach((w) => w.days.forEach((d) => { map[toInput(d.date)] = { ...d, label: w.label }; }));
    return map;
  }, [weeks]);
  const done = useMemo(() => Object.fromEntries(rows.filter((r) => r.source === "riego").map((r) => [r.date, r.id])), [rows]);
  const first = weeks[0]?.days[0]?.date ?? new Date();
  const [month, setMonth] = useState(() => monthStart(new Date() < first ? first : new Date()));

  const status = (key) => {
    const p = plan[key];
    if (done[key]) return p?.water ? "done" : "extra";
    if (!p) return "out";
    if (!p.water) return "rest";
    return key < todayStr ? "missed" : "planned";
  };

  const toggle = (d) => {
    const key = toInput(d);
    if (key > todayStr) { toast("Aún no ha llegado ese día", { description: "Podrás marcarlo cuando riegues." }); return; }
    if (done[key]) {
      setRows((cur) => cur.filter((r) => r.id !== done[key]));
      toast("Riego desmarcado");
      return;
    }
    const p = plan[key];
    const doses = p?.doses ?? [];
    setRows((cur) => [{ id: Date.now(), source: "riego", date: key, type: doses.length ? "Riego con abono" : "Riego", week: p?.label ?? "",
      liters: "", ec: "", ph: "", height: "", note: doses.map((x) => `${x.name} ${x.dose} mL/L`).join(" · ") }, ...cur]);
    toast.success("Riego registrado", { description: "Guardado también en tu diario de Mi cultivo." });
  };

  const days = monthDays(month);
  const inMonth = days.filter(Boolean).map((d) => toInput(d)).filter((k) => k <= todayStr);
  const planned = inMonth.filter((k) => plan[k]?.water);
  const made = planned.filter((k) => done[k]).length;
  const missed = planned.length - made;
  const extra = inMonth.filter((k) => done[k] && !plan[k]?.water).length;

  return (
    <div id="registro-riegos" data-testid="water-calendar" className="rounded-[10px] label-frame bg-[#0E2A1C] p-4 md:p-6 mb-8 md:max-w-[820px]">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="font-mono-d text-[11px] tracking-[0.2em] uppercase text-[#F2B441] flex items-center gap-2"><CalendarCheck className="w-4 h-4" /> Registro de riegos</p>
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Mes anterior" data-testid="water-calendar-prev"
            className="w-9 h-9 grid place-items-center rounded-full border border-[#F6EEDC]/25 hover:border-[#F2B441] transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <p data-testid="water-calendar-month" className="font-serif-d font-black text-lg min-w-[150px] text-center first-letter:uppercase">{month.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}</p>
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Mes siguiente" data-testid="water-calendar-next"
            className="w-9 h-9 grid place-items-center rounded-full border border-[#F6EEDC]/25 hover:border-[#F2B441] transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div data-testid="water-calendar-summary" className="flex flex-wrap gap-2 mb-4 text-sm">
        <span className="rounded-full bg-[#8CD05A]/15 text-[#8CD05A] px-3 py-1"><b data-testid="water-calendar-done">{made}</b> de {planned.length} riegos hechos</span>
        <span className="rounded-full bg-[#E0624B]/15 text-[#F09A88] px-3 py-1"><b data-testid="water-calendar-missed">{missed}</b> olvidados</span>
        {extra > 0 && <span className="rounded-full bg-[#F6EEDC]/10 text-[#E3EADF] px-3 py-1"><b>{extra}</b> extra</span>}
      </div>
      <div className="grid grid-cols-7 gap-1.5 md:gap-2 max-w-3xl">
        {WD.map((w) => <p key={w} className="text-center font-mono-d text-[11px] text-[#C9D6C2] pb-1">{w}</p>)}
        {days.map((d, i) => {
          if (!d) return <span key={`b${i}`} />;
          const key = toInput(d);
          const st = status(key);
          return (
            <button key={key} onClick={() => toggle(d)} data-testid={`water-calendar-day-${key}`} data-status={st}
              aria-label={`${d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}: ${st}`}
              className={`relative aspect-square md:aspect-auto md:h-14 rounded-[6px] border text-sm font-semibold flex flex-col items-center justify-center transition-colors hover:border-[#F2B441] ${tone[st]} ${key === todayStr ? "ring-2 ring-[#F2B441]" : ""}`}>
              {d.getDate()}
              {(st === "done" || st === "extra") && <Check className="w-3.5 h-3.5 md:w-4 md:h-4" />}
              {(st === "planned" || st === "missed") && <Droplets className="w-3 h-3 md:w-3.5 md:h-3.5 opacity-80" />}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <Legend />
        <p className="text-[11px] text-[#C9D6C2]">Toca un día para marcar o desmarcar el riego. Se guarda en tu diario.</p>
      </div>
    </div>
  );
};
